import { Injectable } from "@angular/core";
import { Router, ActivatedRoute } from "@angular/router";

@Injectable({
  providedIn: "root",
})
export class DashboardNavigationService {
  constructor(private router: Router) {}

  /**
   * Navigate to a dashboard route with the current siteId
   * @param route The route to navigate to (without the /widgets/dashboard/:siteId prefix)
   * @param siteId The site ID to use in the route
   * @param queryParams Optional query parameters
   */
  navigateToDashboardRoute(
    route: string,
    siteId: string | null,
    queryParams: Record<string, string | number | boolean | null> = {},
  ): Promise<boolean> {
    if (!siteId) {
      console.error("Cannot navigate to dashboard route: siteId is null");
      return Promise.resolve(false);
    }

    // Ensure the route doesn't start with a slash
    const normalizedRoute = route.startsWith("/") ? route.substring(1) : route;

    // Construct the full route with the widgets/dashboard/:siteId prefix
    const fullRoute = `/widgets/dashboard/${siteId}/${normalizedRoute}`;

    return this.router.navigate([fullRoute], { queryParams });
  }

  /**
   * Get the dashboard route with the current siteId
   * @param route The route to get (without the /widgets/dashboard/:siteId prefix)
   * @param siteId The site ID to use in the route
   * @returns The full route with the widgets/dashboard/:siteId prefix
   */
  getDashboardRoute(route: string, siteId: string | null): string {
    if (!siteId) {
      console.error("Cannot get dashboard route: siteId is null");
      return "";
    }

    // Ensure the route doesn't start with a slash
    const normalizedRoute = route.startsWith("/") ? route.substring(1) : route;

    // Construct the full route with the widgets/dashboard/:siteId prefix
    return `/widgets/dashboard/${siteId}/${normalizedRoute}`;
  }

  /**
   * Extract the siteId from the current route
   * @param route The activated route
   * @returns The siteId from the route parameters, or null if not found
   */
  extractSiteId(route: ActivatedRoute): string | null {
    let currentRoute = route;
    while (currentRoute.firstChild) {
      currentRoute = currentRoute.firstChild;
    }

    const siteId = currentRoute.snapshot.paramMap.get("siteId");
    return siteId;
  }
}
